import {
  MatchingOffers,
  MatchOptions,
  MatchTranche,
  TrancheKey,
  TrancheKeyPeriod,
  trancheKeys,
  TrancheKeyType,
  TrancheKeyValueType
} from 'types/match'

export const parseMatchOptions = (newData: MatchingOffers | null): MatchOptions & MatchTranche => {
  const options: MatchOptions = newData?.options ?? ({} as MatchOptions)
  const tranche: MatchTranche = newData?.tranche ?? ({} as MatchTranche)

  return {
    rdn_pricing_method: options.rdn_pricing_method ?? {
      kind: 'CALENDAR_MOVING_AVERAGE',
      window_days: 1,
      history_years: 3
    },
    pricing_cutoff_date: options.pricing_cutoff_date,
    rdn_energy_pricing_extra_percent: options.rdn_energy_pricing_extra_percent ?? 0,
    tranche_energy_pricing_extra_percent: options.tranche_energy_pricing_extra_percent ?? 0,
    cost_guarantee_of_source: options.cost_guarantee_of_source ?? 10,
    cost_excise: options.cost_excise ?? 5,
    cost_rdn_handling: options.cost_rdn_handling ?? 23,
    cost_ppa_handling: options.cost_ppa_handling ?? 21,
    cost_tranche_handling: options.cost_tranche_handling ?? 18,
    custom_current_energy_price:
      options.custom_current_energy_price ??
      newData?.yearly_data.cost_details['GENERAL'].current_energy_price,
    ...Object.fromEntries(
      trancheKeys.map(key => [[`${key}_power`], tranche?.[`${key}_power`] ?? 0])
    ),
    ...Object.fromEntries(
      trancheKeys.map(key => [[`${key}_price`], tranche?.[`${key}_price`] ?? 0])
    ),
    custom_offers_data: newData?.matched_offers_data.map(offerData => ({
      ...offerData,
      ...(options.custom_offers_data?.find(({ offer_id }) => offer_id === offerData.offer_id) ?? {
        lock: true
      })
    }))
  }
}

export const getDaysByTrancheKey = (key: TrancheKey): number => {
  const [, period, periodValue] = parseTrancheKey(key)
  console.log('🚀 ~ getDaysByTrancheKey ~ period:', key, period, periodValue)
  switch (period) {
    case 'y':
      return 365
    case 'q':
      return getQuarterDays(periodValue ?? 0)
    case 'm':
    default:
      return getMonthDays(periodValue ?? 0)
  }
}

export const parseTrancheKey = (
  key: TrancheKey
): [TrancheKeyType, TrancheKeyPeriod, number | undefined, TrancheKeyValueType] => {
  const [type, period, valueType] = key.split('_')
  const [periodType, periodValue] = period.split('')
  return [
    type as TrancheKeyType,
    periodType as TrancheKeyPeriod,
    periodValue ? parseInt(periodValue) : undefined,
    valueType as TrancheKeyValueType
  ]
}

export const sumTranchePower = (
  type: TrancheKeyType,
  period: TrancheKeyPeriod,
  tranche?: MatchTranche
): number => {
  const hours = type === 'base' ? 24 : 15

  if (period === 'y') {
    return (tranche?.[`${type}_y_power`] ?? 0) * 365 * hours
  } else if (period === 'm') {
    return Array.from(Array(12).keys()).reduce(
      (acc, month) =>
        acc + (tranche?.[`${type}_m${month + 1}_power`] ?? 0) * getMonthDays(month + 1) * hours,
      0
    )
  } else if (period === 'q') {
    return Array.from(Array(4).keys()).reduce(
      (acc, quarter) =>
        acc +
        (tranche?.[`${type}_q${quarter + 1}_power`] ?? 0) * getQuarterDays(quarter + 1) * hours,
      0
    )
  }

  return 0
}

export const minBaseQPrice = (tranche?: MatchTranche) =>
  Math.min(
    ...([
      tranche?.base_q1_price,
      tranche?.base_q2_price,
      tranche?.base_q3_price,
      tranche?.base_q4_price
    ].filter(price => price && price > 0) as number[])
  )

export const aggrTranchePrice = (
  type: TrancheKeyType,
  period: TrancheKeyPeriod,
  aggrFn: (...values: number[]) => number,
  tranche?: MatchTranche
): number => {
  if (period === 'm') {
    return aggrFn(
      ...Array.from(Array(12).keys())
        .map(month => tranche?.[`${type}_m${month + 1}_price`] ?? 0)
        .filter(price => price > 0)
    )
  } else if (period === 'q') {
    return aggrFn(
      ...Array.from(Array(4).keys())
        .map(quarter => tranche?.[`${type}_q${quarter + 1}_price`] ?? 0)
        .filter(price => price > 0)
    )
  }

  return 0
}

export const getMonthDays = (month: number): number => {
  switch (month) {
    case 1:
      return 31
    case 2:
      return 28 // Assuming it's not a leap year
    case 3:
      return 31
    case 4:
      return 30
    case 5:
      return 31
    case 6:
      return 30
    case 7:
      return 31
    case 8:
      return 31
    case 9:
      return 30
    case 10:
      return 31
    case 11:
      return 30
    case 12:
      return 31
    default:
      throw new Error('Invalid month')
  }
}

export const getQuarterDays = (quarter: number): number => {
  switch (quarter) {
    case 1:
      return getMonthDays(1) + getMonthDays(2) + getMonthDays(3)
    case 2:
      return getMonthDays(4) + getMonthDays(5) + getMonthDays(6)
    case 3:
      return getMonthDays(7) + getMonthDays(8) + getMonthDays(9)
    case 4:
      return getMonthDays(10) + getMonthDays(11) + getMonthDays(12)
    default:
      throw new Error('Invalid quarter')
  }
}
